/*!
 * Copyright Optimus Ride 2019
 */

import firebase from 'firebase';
import { VehicleStatus as GrpcVehicleStatus } from '../../grpc/vehicle_status_pb';
import { snapshotToProtobuf } from '../../utils/firestore';

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;

/**
 * The Firestore Collection ID in which this data resides is 'vehicle_status',
 * and the Document IDs correspond to vehicle IDs.
 */

export enum DriveGear {
    FORWARD = 0,
    REVERSE = 1,
    PARK = 2,
    UNKNOWN = 3,
    OFF = 4, // 'OFF' means a state where the VCB is on but the vehicle is not?
    NEUTRAL = 5,
}

export interface VehicleStatus {
    steering: number; // From -1 to 1
    throttle: number; // From  0 to 1
    brake: number;    // From  0 to 1
    speed: number;    // In miles per hour
    driveGear: DriveGear;
    batteryChargePercent: number; // From 0. to 100., with '-1' indicating no such value is present
}

/* Use this function as the callback function to '.onSnapshot()' to transform firestore
 * data into format to be used for the client applications */
export function toVehicleStatus(data: DocumentSnapshot): VehicleStatus {
    return protobufToVehicleStatus(snapshotToProtobuf(data, GrpcVehicleStatus.deserializeBinary));
}

const SECONDS_PER_HOUR = 3600;
const METERS_PER_MILE = 1609.34;

function protobufToVehicleStatus(vehicleStatus: GrpcVehicleStatus): VehicleStatus {
    return {
        steering: vehicleStatus.getSteering(),
        throttle: vehicleStatus.getThrottle(),
        brake: vehicleStatus.getBrake(),
        speed: vehicleStatus.getSpeed() * SECONDS_PER_HOUR / METERS_PER_MILE, // from meters/sec
        driveGear: toDriveGear(vehicleStatus.getDrivemode()),
        batteryChargePercent: vehicleStatus.getBatterycharge(),
    };
}

function toDriveGear(driveMode: GrpcVehicleStatus.DriveMode): DriveGear {
    switch (driveMode) {
        case GrpcVehicleStatus.DriveMode.FORWARD:
            return DriveGear.FORWARD;
        case GrpcVehicleStatus.DriveMode.REVERSE:
            return DriveGear.REVERSE;
        case GrpcVehicleStatus.DriveMode.PARK:
            return DriveGear.PARK;
        case GrpcVehicleStatus.DriveMode.UNKNOWN:
            return DriveGear.UNKNOWN;
        case GrpcVehicleStatus.DriveMode.NEUTRAL:
            return DriveGear.NEUTRAL;
        case GrpcVehicleStatus.DriveMode.OFF:
            return DriveGear.OFF;
        default:
            throw new Error('Unknown Vehicle Status enum value');
    }
}
